import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar/avatar';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { EntityType } from '@atlaskit/smart-common';
import { highlightMatchedText } from '@atlassian/search-common/highlight-matched-text';
import { getTeamPath, getUserPath } from '@atlassian/search-common/search-result-utils';

import { SearchLink } from '../../../../../common/ui/search-link';
import {
	type EventWithTarget,
	type SearchDropdownElementProps,
	SearchLinkTypes,
} from '../../../../../common/ui/search-link/types';
import { onDropdownResultClicked } from '../../../../../common/utils/events/dropdown-result-clicked';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../../../controllers/analytics';
import {
	useCloudId,
	useOrgId,
	usePrimaryProduct,
} from '../../../../../controllers/store/bootstrap';
import {
	useDropdown,
	usePeopleAndTeamsResults,
	useResultCount,
} from '../../../../../controllers/store/dropdown';
import { useInputQuery } from '../../../../../controllers/store/search';

import { messages } from './messages';

export const PeopleAndTeamsResults = ({
	getElementIndex,
}: SearchDropdownElementProps): React.JSX.Element => {
	const intl = useIntl();

	const [cloudId] = useCloudId();
	const [orgId] = useOrgId();
	const [inputQuery] = useInputQuery();
	const [primaryProduct] = usePrimaryProduct();
	const [, { setShowDropdown }] = useDropdown();
	const [resultCount] = useResultCount();
	const [peopleAndTeamsResults] = usePeopleAndTeamsResults();

	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();

	const fireAnalytics = useCallback(
		(event: EventWithTarget) => {
			const index = event.target && getElementIndex ? getElementIndex(event.target) : undefined;

			fireAnalyticsEvent(
				onDropdownResultClicked({
					commonAttributes,
					nonPrivacySafeAttributes,
					resultType: SearchLinkTypes.PeopleAndTeams,
					duration: peopleAndTeamsResults.duration,
					...(index !== undefined ? { index } : {}),
				}),
			);
		},
		[
			commonAttributes,
			fireAnalyticsEvent,
			getElementIndex,
			nonPrivacySafeAttributes,
			peopleAndTeamsResults.duration,
		],
	);

	const onClick = (event: EventWithTarget) => {
		setShowDropdown(false);
		fireAnalytics(event);
	};

	return (
		<>
			{peopleAndTeamsResults.results.slice(0, resultCount.peopleAndTeams).map((result, index) => {
				if (result.entityType === EntityType.USER && result.name) {
					const navigationUrl =
						primaryProduct &&
						cloudId &&
						orgId &&
						getUserPath(result.id, primaryProduct, orgId, cloudId);

					return (
						<PeopleAndTeamsLink
							title={result.name}
							description={result.title}
							avatarUrl={result.avatarUrl}
							onClick={onClick}
							inputQuery={inputQuery}
							key={`${index}-${result.name}`}
							navigationUrl={navigationUrl || ''}
						/>
					);
				} else if (result.entityType === EntityType.TEAM && result.displayName) {
					const navigationUrl =
						primaryProduct &&
						cloudId &&
						orgId &&
						getTeamPath(result.id, primaryProduct, orgId, cloudId);

					return (
						<PeopleAndTeamsLink
							title={result.displayName}
							description={intl.formatMessage(messages.teamLabel)}
							avatarUrl={result?.smallAvatarImageUrl}
							onClick={onClick}
							inputQuery={inputQuery}
							key={`${index}-${result.name}`}
							navigationUrl={navigationUrl || ''}
						/>
					);
				}
			})}
		</>
	);
};

const iconStyles = xcss({
	backgroundColor: 'color.background.neutral',
	borderRadius: 'radius.full',
	width: '24px',
	height: '24px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

type PeopleAndTeamsLinkProps = {
	title: string;
	description?: string;
	avatarUrl?: string;
	inputQuery: string;
	onClick: (event: EventWithTarget) => void;
	key: string;
	navigationUrl: string;
};

const PeopleAndTeamsLink = ({
	title,
	description = '',
	avatarUrl = '',
	inputQuery,
	onClick,
	navigationUrl,
}: PeopleAndTeamsLinkProps) => (
	<SearchLink
		title={title}
		description={description}
		icon={
			<Box xcss={iconStyles}>
				<Avatar src={avatarUrl} size="small" />
			</Box>
		}
		onClick={onClick}
		highlightedTitle={highlightMatchedText(title, inputQuery.split(' ')).highlightedTitle}
		navigationUrl={navigationUrl}
	/>
);
